import { useEffect, useState } from "react";

// =============================================================================
// useExitAnim — keep a component mounted through its EXIT animation.
// =============================================================================
// React unmounts instantly on `isOpen=false`, so an exit keyframe never plays
// (this is why every Altara modal historically just *vanished*). This hook holds
// `mounted=true` for `duration` ms after close and flips `closing=true`, so you
// swap the entrance class for the exit class and unmount only once it finishes.
//
//   const { mounted, closing } = useExitAnim(isOpen, 220);
//   if (!mounted) return null;
//   return (
//     <div className={closing ? "anim-backdrop-out" : "anim-backdrop-in"}>
//       {/* NON-glass wrapper carries the transform (glass rule) */}
//       <div className={closing ? "anim-modal-out" : "anim-modal-in"}>
//         <div className="detail-overlay">…</div>
//       </div>
//     </div>
//   );
//
// `duration` MUST match the exit keyframe length (anim-modal-out = 200ms,
// anim-drawer-out = 300ms). Pair with `/animate` toolkit exit classes.
// =============================================================================

export function useExitAnim(
  isOpen: boolean,
  duration = 220
): { mounted: boolean; closing: boolean } {
  const [mounted, setMounted] = useState(isOpen);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setClosing(false);
      return;
    }
    // isOpen just went false — play the exit, then unmount.
    setClosing((wasClosing) => {
      if (!mounted) return wasClosing;
      return true;
    });
    if (!mounted) return;
    const t = setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, duration);
    return () => clearTimeout(t);
    // `mounted` intentionally omitted: we only react to isOpen flips, and read
    // the latest `mounted` via closure. Adding it would restart the exit timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, duration]);

  return { mounted, closing };
}
